import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useLang } from "@/lib/i18n";
import { Music2, Calendar, Ticket } from "lucide-react";

const WEEKLY = [
  { day: "Mon", titleKey: "week.mon.title", genresKey: "week.mon.genres", subKey: "week.mon.sub", c1: "#FF1B8D", c2: "#FF6BC6" },
  { day: "Tue", titleKey: "week.tue.title", genresKey: "week.tue.genres", subKey: "week.tue.sub", c1: "#FFC857", c2: "#FF6BC6" },
  { day: "Wed", titleKey: "week.wed.title", genresKey: "week.wed.genres", subKey: "week.wed.sub", c1: "#A655F2", c2: "#FF1B8D" },
  { day: "Thu", titleKey: "week.thu.title", genresKey: "week.thu.genres", subKey: "week.thu.sub", c1: "#FF6BC6", c2: "#FFC857" },
  { day: "Fri", titleKey: "week.fri.title", genresKey: "week.fri.genres", subKey: "week.fri.sub", c1: "#5BC0FF", c2: "#A655F2" },
  { day: "Sat", titleKey: "week.sat.title", genresKey: "week.sat.genres", subKey: "week.sat.sub", c1: "#FF1B8D", c2: "#FFC857", featured: true },
  { day: "Sun", titleKey: "week.sun.title", genresKey: "week.sun.genres", subKey: "week.sun.sub", c1: "#FFC857", c2: "#FF1B8D" },
];

const WEEKLY_STYLES = {
  Mon: { c1: "#FF1B8D", c2: "#FF6BC6" },
  Tue: { c1: "#FFC857", c2: "#FF6BC6" },
  Wed: { c1: "#A655F2", c2: "#FF1B8D" },
  Thu: { c1: "#FF6BC6", c2: "#FFC857" },
  Fri: { c1: "#5BC0FF", c2: "#A655F2" },
  Sat: { c1: "#FF1B8D", c2: "#FFC857" },
  Sun: { c1: "#FFC857", c2: "#FF1B8D" },
};
const DAY_LABEL = { mon: "Mon", tue: "Tue", wed: "Wed", thu: "Thu", fri: "Fri", sat: "Sat", sun: "Sun" };

export default function Events() {
  const { t, lang } = useLang();
  const [events, setEvents] = useState([]);
  const [weekly, setWeekly] = useState([]);
  useEffect(() => {
    api.get("/events").then((r) => setEvents(r.data)).catch(() => {});
    api.get("/weekly-lineup").then((r) => setWeekly(r.data)).catch(() => {});
  }, []);
  const dlocale = lang === "es" ? "es-ES" : "en-GB";

  return (
    <div data-testid="events-page" className="max-w-[1400px] mx-auto px-4 md:px-12 pt-12 md:pt-20 pb-24 md:pb-32 overflow-x-hidden">
      {/* ── WEEKLY LINE-UP (grunge title only — replaces old text header) ── */}
      <section data-testid="events-weekly-lineup" className="mt-2 md:mt-4">
        {/* Inline chroma-key filter (drops the black background of the title PNG) */}
        <svg width="0" height="0" style={{ position: "absolute" }} aria-hidden="true">
          <defs>
            <filter id="soho-chroma-events" colorInterpolationFilters="sRGB">
              <feColorMatrix
                type="matrix"
                values="1 0 0 0 0
                        0 1 0 0 0
                        0 0 1 0 0
                        2 2 2 0 -1.2"
              />
              <feComposite in="SourceGraphic" in2="alpha" operator="in" />
            </filter>
          </defs>
        </svg>
        <div className="mb-6 md:mb-8 text-center">
          <img loading="lazy" decoding="async"             src="https://customer-assets.emergentagent.com/job_soho69-events/artifacts/xupotvzy_4B2E6660-F3D6-4EE4-B1E3-F55ED0DF00F0.png"
            alt="Soho69 Weekly Line-Up"
            data-testid="events-weekly-title"
            draggable={false}
            className="block mx-auto w-full max-w-[380px] md:max-w-[560px] h-auto select-none"
            style={{
              filter:
                "url(#soho-chroma-events) saturate(1.1) contrast(1.05) drop-shadow(0 0 18px rgba(255,27,141,0.55)) drop-shadow(0 0 50px rgba(255,27,141,0.4))",
            }}
          />
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-7 gap-3">
          {weekly.map((w) => {
            const dayLabel = DAY_LABEL[w.day] || w.day;
            const style = WEEKLY_STYLES[dayLabel] || { c1: "#FF1B8D", c2: "#FF6BC6" };
            return (
            <Link
              to={`/book?type=barorder`}
              key={w.day}
              data-testid={`events-weekly-${w.day}`}
              className={
                "relative rounded-3xl overflow-hidden p-5 md:p-4 flex flex-col min-h-[210px] md:min-h-[280px] transition hover:scale-[1.03] " +
                (w.is_featured ? "ring-glow border border-neon" : "border border-white/10")
              }
              style={{ background: `linear-gradient(160deg, ${style.c1} 0%, ${style.c2} 100%)` }}
            >
              <div className="absolute inset-0 bg-black/55 pointer-events-none" />
              <div className="absolute inset-0 bg-noise opacity-40 mix-blend-overlay pointer-events-none" />
              <div className="relative">
                <div className="text-[10px] font-bold uppercase tracking-[0.32em] text-white/85">{dayLabel}</div>
                <div className="font-heading text-2xl md:text-xl xl:text-[1.4rem] text-white mt-2 leading-tight">
                  {w.title}
                </div>
              </div>
              <div className="relative mt-3 pt-2 flex-1 flex flex-col">
                <div className="text-[11.5px] md:text-[11px] text-white italic font-semibold mt-auto pt-3">
                  &ldquo;{w.subtitle}&rdquo;
                </div>
              </div>
              {w.is_featured && (
                <span className="absolute top-3 right-3 text-[9px] font-bold uppercase tracking-widest text-black px-2 py-0.5 rounded-full bg-neon">
                  ★
                </span>
              )}
            </Link>
            );
          })}
        </div>
      </section>

      <div className="mt-16 md:mt-20 mb-6 md:mb-8">
        <div className="text-[10px] uppercase tracking-[0.32em] text-neon font-bold">{t("events.specialKicker")}</div>
        <h1 className="h-display text-4xl md:text-6xl mt-2">{t("events.specialTitle")}</h1>
      </div>

      <div className="grid md:grid-cols-2 gap-5 md:gap-6">
        {events.length === 0 && (
          <div className="text-white/50 py-12 col-span-2 text-center glass rounded-4xl">{t("events.empty")}</div>
        )}
        {events.map((e, i) => (
          <div key={e.id} data-testid={`event-card-${i}`}
               className="group relative surface rounded-4xl p-6 md:p-8 overflow-hidden hover:border-neon/60 transition">
            <div className="absolute -top-12 -right-12 w-40 h-40 rounded-full bg-neon/10 blur-3xl group-hover:bg-neon/20 transition" />
            {e.is_featured && (
              <span className="absolute top-4 right-4 text-[10px] uppercase tracking-[0.32em] text-plum-900 px-2.5 py-1 rounded-full font-bold"
                    style={{ background: "linear-gradient(135deg,#FFC857,#FF8B42)" }}>
                {t("events.featured")}
              </span>
            )}
            <div className="flex items-center gap-2 text-neon text-xs uppercase tracking-[0.28em]">
              <Calendar size={14} />
              {new Date(e.date).toLocaleDateString(dlocale, { weekday: "long", day: "2-digit", month: "long" })}
            </div>
            <h3 className="font-heading text-3xl md:text-5xl uppercase mt-3 break-words">{e.title}</h3>
            <div className="text-white/70 mt-2 flex items-center gap-2">
              <Music2 size={16} className="text-neon shrink-0" /> {e.dj}
            </div>
            {e.description && <p className="text-white/60 mt-4 text-sm">{e.description}</p>}
            <div className="mt-6 flex flex-wrap gap-3">
              <Link to={`/book?type=ticket&event=${encodeURIComponent(e.title)}&date=${e.date}`}
                    data-testid={`event-ticket-btn-${i}`}
                    className="btn-pink !py-3 !px-5 text-[10px] md:text-xs">
                <Ticket size={14} /> {t("events.getTickets")}
              </Link>
              <Link to={`/book?type=barorder`}
                    data-testid={`event-table-btn-${i}`}
                    className="btn-glass !py-3 !px-5 text-[10px] md:text-xs">
                {t("events.bookTable")}
              </Link>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
